import cn from 'classnames'
import * as R from 'ramda'
import { useCallback, useMemo } from 'react'

import { splitPathsByCommonPrefix } from 'components/formKit'
import { useFormDefaultValues, useFormOnChangeValue, useFormValues } from 'components/formKit/hooks'
import type { IFieldBaseRenderProps } from 'components/formKit/types'
import { testProps } from 'utils/test/qaData'

import * as s from './LabelResetDefault.module.scss'

const LabelResetDefault: React.FC<IFieldBaseRenderProps> = ({ config, configs }) => {
  const onChangeValue = useFormOnChangeValue()
  const formValues = useFormValues()
  const formDefaultValues = useFormDefaultValues()

  const { isActive, name, defaultValue } = useMemo(() => {
    if (configs?.length) {
      const names = configs.map(({ name }) => name)
      const splitted = R.map(R.split('.'), names)
      const values = splitted.map((path) => R.path(path, formValues))
      const defaultValues = splitted.map((path) => R.path(path, formDefaultValues))
      const isActive = !R.equals(values, defaultValues)
      const { sharedPath, relativePaths } = splitPathsByCommonPrefix(names)

      const parentValue = R.path(sharedPath, formValues)
      const parentDefaultValue = R.path(sharedPath, formDefaultValues)
      const defaultValue = R.reduce(
        (acc, path) => R.assocPath(path, R.path(path, parentDefaultValue), acc),
        parentValue,
        relativePaths,
      )
      return {
        isActive,
        name: sharedPath.join('.'),
        defaultValue,
      }
    }

    const value = R.path(config.name.split('.'), formValues)
    const defaultValue = R.path(config.name.split('.'), formDefaultValues)
    const isActive = !R.equals(value, defaultValue)
    return {
      isActive,
      name: config.name,
      defaultValue: defaultValue !== undefined ? defaultValue : config.defaultValue,
    }
  }, [configs, config.name, config.defaultValue, formValues, formDefaultValues])

  const onReset = useCallback(() => {
    if (isActive) {
      return onChangeValue?.({
        value: defaultValue,
        name: name,
        meta: { dirty: true },
      })
    }
  }, [onChangeValue, isActive, defaultValue, name])

  if (!config.label) {
    return null
  }

  return (
    <div
      {...testProps({ el: 'resettableLabel', label: config.label })}
      className={cn(s.label, { [s.active]: isActive })}
      onClick={onReset}
    >
      {config.label} {isActive && <span className={s.indicator} />}
    </div>
  )
}
export default LabelResetDefault
